<template>
  <div class="bricks-editor">

    <div class="bricks-list" ref="bricksList">
      
      <nz-brick
        v-for="(brick, index) in bricks"
        :key="brick.id"
        :brick="brick"
        :index="index"
        :fixed-count="fixedcptypes.length"
        :total-bricks="bricks.length"
        @updated="reloadAfterUpdate"
        @edit="openEditor"
        @removed="removeBrickFromList"
        @addbrick="handleAddBrick"
      />
      
    </div>

  </div>
</template>

<script>
import NzBrick from "./bricks/NzBrick.vue";
import Sortable from "sortablejs";
import { loadCustomerConfig } from "@/utils/loadCustomerConfig.js";

export default {
  name: "BricksEditor",

  components: { NzBrick },

  props: {
    article: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      bricks: [],
      fixedcptypes: [],
      defaults: {},
      blockname: "maincontent"
    };
  },

  async mounted() {
    await this.loadCustomerConfig();
    await this.loadBricks();
    this.$nextTick(() => this.initDragDrop());
  },

  methods: {
    /* ───────────────────────────────────────────────
       LOAD CUSTOMER CONFIG (via shared loader)
    ─────────────────────────────────────────────── */
    async loadCustomerConfig() {
      try {
        const cfg = await loadCustomerConfig();

        this.fixedcptypes   = cfg?.articles?.fixedcptypes   || [];
        this.defaults = cfg?.articles?.defaults || {};
        this.blockname      = cfg?.articles?.blockname      || "maincontent";

      } catch (err) {
        console.warn("⚠ Using default customer config:", err);

        this.fixedcptypes   = [];
        this.defaults = {};
        this.blockname = "maincontent";
      }
    },

    /* ───────────────────────────────────────────────
       LOAD BRICKS FROM API
    ─────────────────────────────────────────────── */
    async loadBricks() {
      if (!this.article?.id) return;

      const apiKey = localStorage.getItem("api_key");

      try {
        const res = await fetch(`/api/bricks/article/${this.article.id}`, {
          headers: {
            "Content-Type": "application/json",
            ...(apiKey ? { "x-api-key": apiKey } : {})
          }
        });

        const data = await res.json();
        this.bricks = data.bricks || [];

      } catch (err) {
        console.error("❌ Error loading bricks:", err);
      }
    },

    reloadAfterUpdate() {
      this.$emit("updated");
    },

    openEditor(brick) {
      this.$emit("edit", brick);
    },

    /* ───────────────────────────────────────────────
       ADD BRICK
    ─────────────────────────────────────────────── */
    async handleAddBrick({ afterId, type }) {
      const idx = this.bricks.findIndex(b => b.id === afterId);

      // auto-advance fixed brick types
      const nextFixed = this.fixedcptypes[idx + 1];
      if (nextFixed) type = nextFixed;

      const apiKey = localStorage.getItem("api_key");
      const newSort = (idx + 2) * 10;

      const payload = {
        article_id: this.article.id,
        lang: this.defaults.lang,
        cptype: type,
        cptitle: this.defaults.content.cptitle || "",
        cptext: this.defaults.content.cptext || "",
        cpimage: this.defaults.content.cpimage || "",
        cplink: "",
        cpappfilelink: "",
        cpacc: "",
        cptable: "",
        cpsound: "",
        cpvideo: "",
        cpvideoyt: "",
        blockname: this.blockname,
        sortorder: newSort
      };

      try {
        const res = await fetch(`/api/bricks`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(apiKey ? { "x-api-key": apiKey } : {})
          },
          body: JSON.stringify(payload)
        });

        const { brick: newBrick } = await res.json();

        this.bricks.splice(idx + 1, 0, {
          ...newBrick,
          autofocus: true
        });

        this.updateSortOrder();

      } catch (err) {
        console.error("❌ Failed to add brick:", err);
      }
    },

    updateSortOrder() {
      this.bricks.forEach((b, i) => {
        b.sortorder = (i + 1) * 10;
      });
      this.saveSortOrder();
    },

    removeBrickFromList(id) {
      const index = this.bricks.findIndex(b => b.id === id);
      if (index < this.fixedcptypes.length) return; // cannot remove fixed
      this.bricks = this.bricks.filter(b => b.id !== id);
      this.updateSortOrder();
    },

    /* ───────────────────────────────────────────────
       SORTABLE DRAG & DROP
    ─────────────────────────────────────────────── */
    initDragDrop() {
      const container = this.$refs.bricksList;
      if (!container) return;

      Sortable.create(container, {
        handle: ".drag-handle",
        animation: 150,
        ghostClass: "drag-ghost",

        filter: ".fixed-brick",
        preventOnFilter: false,

        onMove: evt => {
          const targetFixed = evt.related.classList.contains("fixed-brick");
          return !targetFixed;
        },

        onEnd: evt => {
          if (
            evt.oldIndex < this.fixedcptypes.length ||
            evt.newIndex < this.fixedcptypes.length
          ) return;

          this.reorderBricks(evt.oldIndex, evt.newIndex);
        }
      });
    },

    reorderBricks(oldIndex, newIndex) {
      if (oldIndex === newIndex) return;

      const moved = this.bricks.splice(oldIndex, 1)[0];
      this.bricks.splice(newIndex, 0, moved);

      this.updateSortOrder();
    },

    async saveSortOrder() {
      const apiKey = localStorage.getItem("api_key");

      const payload = this.bricks.map(b => ({
        id: b.id,
        sortorder: b.sortorder
        
      }));

      try {
        await fetch(`/api/bricks/reorder`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(apiKey ? { "x-api-key": apiKey } : {})
          },
          body: JSON.stringify({ bricks: payload })
        });

      } catch (err) {
        console.error("❌ Failed to save sort order:", err);
      }
    }
  }
};
</script>

<style scoped>
.bricks-editor {
  padding: 1rem;
}
.bricks-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.drag-ghost {
  opacity: 0.4;
}
</style>
